/* eslint-disable no-unused-vars */
import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFlexLayout } from 'react-table';
import { List } from 'react-window';
import {
  useHvTable,
  HvTableContainer,
  HvEmptyState,
  useHvResizeColumns,
} from '@hitachivantara/uikit-react-core';

import Loader from '../../Loader';
import { getAlarmsLoading, getAlarmsTotalByTab, requestAlarms } from '../../../bundles/alarms';
import { requestUser } from '../../../bundles/ad';
import { getTimezone } from '../../../bundles/application';
import { formatSiteDatetime } from '../../../datetimeUtils';
import { API_BATCH_SIZE, ROW_HEIGHT, CONTAINER_HEIGHT, HEADER_HEIGHT } from './constants';
import { getColumnsForTab } from './AlarmColumns';
import {
  ALARM_TABS,
  buildAlarmQuery,
  filterAlarmsBySearch,
  formatAlarmName,
  getSeverityColor,
} from './utils';
import config from '../../../config';

const getAlarmStateFromTab = (tab) => {
  switch (tab) {
    case ALARM_TABS.NEW: {
      return 'new';
    }
    case ALARM_TABS.ACKNOWLEDGED: {
      return 'acknowledged';
    }
    default: {
      return 'resolved';
    }
  }
};

// Filter out non-DOM props from react-table props
const filterDomProps = (props) => {
  const {
    key,
    sortable,
    sorted,
    sortDirection,
    resizable,
    resizing,
    resizerProps,
    hover,
    ...domProps
  } = props;
  return domProps;
};

const DEFAULT_COLUMN = {
  minWidth: 50,
  width: 150,
};

const AlarmList = ({
  alarms,
  site,
  activeTab,
  searchText,
  from,
  to,
  onAlarmClick,
  onSortChange,
  sortBy,
  sortOrder,
  deviatingReferenceDesignations,
  includeChildren,
  listColumns = [],
  isFullScreen = false,
}) => {
  const dispatch = useDispatch();
  const isLoading = useSelector(getAlarmsLoading);
  const timezone = useSelector(getTimezone);
  const totalAlarms = useSelector((state) => getAlarmsTotalByTab(state, activeTab));
  const wasResizingRef = useRef(false);
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(null);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        if (height > 0) {
          setContainerHeight(height);
        }
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const formatDate = useCallback(
    (dateValue) => {
      if (!dateValue) return '-';
      return formatSiteDatetime(dateValue, timezone);
    },
    [timezone]
  );

  // Pre-process alarms once at the list level to avoid per-cell lookups
  const enrichedAlarms = useMemo(() => {
    if (!alarms) return [];
    return alarms.map((alarm) => ({
      ...alarm,
      // Pre-compute severity colors once - avoids repeated lookups in cells
      highestSeverityColor: getSeverityColor(alarm.highestSeverity),
      latestSeverityColor: getSeverityColor(alarm.latestSeverity),
      // Pre-parse dates once - avoids repeated formatting in cells
      formattedLastOccurrence: formatDate(alarm.lastOccurrence),
      formattedAcknowledgedAt: formatDate(alarm.acknowledgedAt),
      formattedResolvedAt: formatDate(alarm.resolvedAt),
      displayAlarmName: formatAlarmName(alarm),
      displayEquipment: alarm.deviatingReferenceDesignation || '',
    }));
  }, [alarms, formatDate]);

  useEffect(() => {
    if (!alarms || alarms.length === 0) return;
    // Only request user details when ack/resolved columns are visible (not excluded) and not suppressed by onPrem
    const shouldRequestUsers =
      !config.onPremise &&
      (!listColumns.includes('acknowledgedBy') || !listColumns.includes('resolvedBy'));
    if (!shouldRequestUsers) return;
    const userIds = new Set();
    alarms.forEach((alarm) => {
      if (alarm?.acknowledgedBy) {
        userIds.add(alarm.acknowledgedBy);
      }
      if (alarm?.resolvedBy) {
        userIds.add(alarm.resolvedBy);
      }
    });
    userIds.forEach((userId) => dispatch(requestUser(userId)));
  }, [alarms, dispatch, listColumns]);

  const moreAlarmsInApi = enrichedAlarms?.length < totalAlarms;

  // Handle loading more when user scrolls near the end
  const handleRowsRendered = useCallback(
    (visibleRows) => {
      const visibleStopIndex = visibleRows?.stopIndex;
      if (!moreAlarmsInApi || isLoading) return;

      const threshold = Math.min(10, enrichedAlarms.length - 1);

      if (visibleStopIndex >= enrichedAlarms.length - threshold) {
        const query = buildAlarmQuery({
          site,
          state: getAlarmStateFromTab(activeTab),
          offset: enrichedAlarms.length,
          limit: API_BATCH_SIZE,
          from,
          to,
          sortBy,
          sortOrder,
          deviatingReferenceDesignations,
          includeChildren,
        });

        if (config.onPremise) {
          delete query.site;
          delete query.org;
        }

        dispatch(requestAlarms(query));
      }
    },
    [
      moreAlarmsInApi,
      isLoading,
      enrichedAlarms,
      dispatch,
      activeTab,
      site,
      from,
      to,
      sortBy,
      sortOrder,
      deviatingReferenceDesignations,
      includeChildren,
    ]
  );

  const filteredAlarms = useMemo(() => {
    return filterAlarmsBySearch(enrichedAlarms, searchText);
  }, [enrichedAlarms, searchText]);

  const listHeight = (containerHeight || CONTAINER_HEIGHT) - HEADER_HEIGHT;

  const columns = useMemo(() => {
    return getColumnsForTab(activeTab, listColumns, config.onPremise);
  }, [activeTab, listColumns]);

  const { getTableHeadProps, headerGroups, rows, prepareRow, state } = useHvTable(
    {
      columns,
      data: filteredAlarms,
      defaultColumn: DEFAULT_COLUMN,
    },
    useFlexLayout,
    useHvResizeColumns
  );

  const isResizing = !!state.columnResizing?.isResizingColumn;

  useEffect(() => {
    if (!isResizing && wasResizingRef.current) {
      const timer = setTimeout(() => {
        wasResizingRef.current = false;
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isResizing]);

  // Row component for react-window v2 - rowProps are spread directly onto the component
  const AlarmRow = useCallback(
    ({ index, style, rows: tableRows, prepareRow: prepare, onRowClick }) => {
      const row = tableRows?.[index];
      if (!row) return null;
      prepare(row);

      const alarm = row.original;

      return (
        <div
          role="button"
          tabIndex={0}
          style={{ ...style, cursor: 'pointer' }}
          className="alarm-row"
          onClick={() => onRowClick?.(alarm)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onRowClick?.(alarm);
            }
          }}
        >
          {row.cells.map((cell) => {
            const cellProps = filterDomProps(cell.getCellProps());
            return (
              <div
                key={cell.column.id}
                className="alarm-cell"
                {...cellProps}
              >
                {cell.render('Cell')}
              </div>
            );
          })}
        </div>
      );
    },
    []
  );

  // Data passed to rows via rowProps
  // Include columnResizing state to force row re-renders when columns resize
  const columnResizing = state.columnResizing;
  // Create a key that changes when column widths change to force List re-render
  const resizeKey = useMemo(
    () => JSON.stringify(columnResizing?.columnWidths || {}),
    [columnResizing?.columnWidths]
  );
  const rowData = useMemo(
    () => ({
      rows,
      prepareRow,
      onRowClick: onAlarmClick,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, prepareRow, onAlarmClick, columnResizing]
  );

  const headers = (
    <div
      {...filterDomProps(getTableHeadProps?.() || {})}
      className="alarm-header"
    >
      {headerGroups.map((headerGroup, index) => {
        const headerGroupProps = filterDomProps(headerGroup.getHeaderGroupProps());
        return (
          <div
            {...headerGroupProps}
            key={`${headerGroup.id}-${index}`}
          >
            {headerGroup.headers.map((col) => {
              const resizerProps = col.getResizerProps?.() || {};
              const headerProps = filterDomProps(col.getHeaderProps());
              const isSortable = col.sortable !== false;
              const isSorted = sortBy === col.id;

              const originalOnClick = headerProps.onClick;
              const wrappedHeaderProps = {
                ...headerProps,
                onClick: (event) => {
                  if (wasResizingRef.current || isResizing) {
                    event.stopPropagation();
                    return;
                  }
                  if (isSortable && onSortChange) {
                    onSortChange(col.id);
                  }
                  originalOnClick?.(event);
                },
                style: {
                  ...headerProps.style,
                  cursor: isSortable ? 'pointer' : 'default',
                },
              };

              return (
                <div
                  {...wrappedHeaderProps}
                  key={col.id}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {col.render('Header')}
                    {isSortable && isSorted && (
                      <span style={{ fontSize: '12px' }}>
                        {sortOrder === 'ASC' ? '\u25B2' : '\u25BC'}
                      </span>
                    )}
                  </div>
                  {col.getResizerProps && (
                    <div
                      {...resizerProps}
                      onMouseDown={(event) => {
                        wasResizingRef.current = true;
                        resizerProps.onMouseDown?.(event);
                      }}
                      onTouchStart={(event) => {
                        wasResizingRef.current = true;
                        resizerProps.onTouchStart?.(event);
                      }}
                      className={`resizer ${col.isResizing ? 'isResizing' : ''}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="alarm-list">
      <div className="alarm-table-section">
        <HvTableContainer className={`alarm-table-container ${isFullScreen ? 'full-screen' : ''}`}>
          <div
            ref={containerRef}
            className="alarm-table-inner"
          >
            {isLoading && filteredAlarms.length === 0 ? (
              <div className="alarm-list-loader-container">
                <Loader />
              </div>
            ) : (
              <div>
                {rows.length > 0 ? (
                  <>
                    {headers}
                    <List
                      key={resizeKey}
                      style={{ width: '100%', height: listHeight }}
                      rowCount={rows.length}
                      rowHeight={ROW_HEIGHT}
                      rowComponent={AlarmRow}
                      rowProps={rowData}
                      onRowsRendered={handleRowsRendered}
                      className="alarm-body custom-scrollbar"
                    />
                  </>
                ) : (
                  <div className="alarm-empty-state">
                    <HvEmptyState message="No alarms to display." />
                  </div>
                )}

                {isLoading && filteredAlarms.length > 0 && (
                  <div className="load-more-loader">
                    <Loader />
                  </div>
                )}
              </div>
            )}
          </div>
        </HvTableContainer>
      </div>
    </div>
  );
};

export default AlarmList;
